import { useState } from "react";

import { SubscriptionDefaultIcon } from "@jti/brand-icons/subscription-default";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { Separator } from "@jti/core-components/components/Separator";
import { useTranslations } from "@jti/ui";

import { SubscriptionBillingDisclaimer } from "@checkout-v2/shared";
import { useCheckoutSubscriptionDisplayState } from "@checkout-v2/shared/hooks";
import { getCartItemsCount } from "@checkout-v2/shared/utils/getCartItemsCount";

import { PointsDisclaimer } from "./PointsDisclaimer";
import { SubscriptionBenefits } from "./SubscriptionBenefits";
import { SubscriptionPrices } from "./SubscriptionPrices";
import { SubscriptionProductCard } from "./SubscriptionProductCard";

import styles from "./SubscriptionSummary.module.scss";

// TODO - logic for the number of points is not implemented yet.
// this is a placeholder for real values
const MOCK_POINTS_DISCLAIMER_SECTION_CONDITION = false;
const MOCK_NUMBER_OF_POINTS = 0;

type SummaryCartItemsProps = {
  isCollapsible?: boolean;
  showProductList?: boolean;
};

const SubscriptionSummaryTitle = ({ itemsCount }: { itemsCount: number }) => {
  const { __ } = useTranslations();

  return (
    <span className={styles.subscriptionCardTitleContainer}>
      <SubscriptionDefaultIcon className={styles.subscriptionIcon} />
      <span className={styles.subscriptionCardTitleText}>{__("summarySubscription.title", [itemsCount])}</span>
    </span>
  );
};

export const SubscriptionSummary = ({ isCollapsible = true, showProductList = true }: SummaryCartItemsProps) => {
  const { __ } = useTranslations();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { subscriptionItems, showSubscriptionSummary, showSidebarBillingDisclaimer } =
    useCheckoutSubscriptionDisplayState();

  if (!showSubscriptionSummary) {
    return null;
  }

  const toggleCollapse = () => setIsCollapsed((currentCollapsedState) => !currentCollapsedState);

  return (
    <Card variant="level-2">
      <SubscriptionSummaryTitle itemsCount={getCartItemsCount(subscriptionItems)} />

      <div className={styles.subscriptionCardBodyContainer}>
        {showProductList && isCollapsible && (
          <Button className={styles.collapseButton} variant="text" onClick={toggleCollapse}>
            {isCollapsed
              ? __("cart.checkout.summary.subscription.show_products")
              : __("cart.checkout.summary.subscription.hide_products")}
          </Button>
        )}

        {showProductList && !isCollapsed && (
          <ul className={styles.subscriptionProductsList}>
            {subscriptionItems.map((item) => (
              <li key={item.sku}>
                <SubscriptionProductCard {...item} />
              </li>
            ))}
          </ul>
        )}

        <div className={styles.separatorOverrides}>
          <Separator />
        </div>
        <SubscriptionBenefits />
        <div className={styles.separatorOverrides}>
          <Separator />
        </div>
        <SubscriptionPrices />
        {showSidebarBillingDisclaimer && <SubscriptionBillingDisclaimer />}

        {MOCK_POINTS_DISCLAIMER_SECTION_CONDITION && (
          <>
            <div className={styles.separatorOverrides}>
              <Separator />
            </div>
            <PointsDisclaimer numberOfPoints={MOCK_NUMBER_OF_POINTS} />
          </>
        )}
      </div>
    </Card>
  );
};
